import { Check, MessageSquare, X } from 'lucide-react';
import { useState } from 'react';
import { useEVMTransaction } from '../../../../hooks/useEvmTransaction';

import { decodeHexMessage } from '../../../../lib/evm/decode';
import { classifyTransaction } from '../../../../lib/evm/transaction';
import { createDeclineTransactionMessage } from '../../../../lib/transaction-messages';
import { SigningCardFooter } from './SigningCardFooter';

import { isHex } from 'viem';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/accordion';
import { CardHeader } from '../../../ui/card';
import { CopyStandard } from '../../CopyStandard';
import DefaultTxApproveButton from '../../default-components/DefaultTxApproveButton';
import DefaultTxContainer from '../../default-components/DefaultTxContainer';
import DefaultTxDeclineButton from '../../default-components/DefaultTxDeclineButton';
import LoadingMessage from '../../loading/LoadingMessage';
import { TransactionDetail } from '../TransactionDetail';
import { EvmOperationCardProps } from './types';

export const EvmSignCard = ({
  evmData,
  messageBackgroundColor,
  borderColor,
  textColor,
  customTxContainer: TxContainer = DefaultTxContainer,
  customApproveTxButton: ApproveButton = DefaultTxApproveButton,
  customDeclineTxButton: DeclineButton = DefaultTxDeclineButton,
  append,
  messageId,
  classification: providedClassification,
}: EvmOperationCardProps) => {
  const {
    isLoading,
    errorMsg,
    transaction,
    handleSmartAction,
    network,
    evmAddress,
  } = useEVMTransaction({ evmData, append, messageId });

  const [isDeclined, setIsDeclined] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  // Use provided classification or compute it
  const classification = providedClassification ?? classifyTransaction(evmData);
  const { method, params } = evmData;

  // Determine the type of signing request
  const isMessage = method === 'personal_sign' || method === 'eth_sign';
  const isTypedData =
    method === 'eth_signTypedData_v4' || method === 'eth_signTypedData';

  const isSuccess = Boolean(
    transaction && transaction.signature && transaction.status === 'success',
  );

  const declineTransaction = async () => {
    setIsDeclining(true);
    try {
      await append(createDeclineTransactionMessage(messageId));
      setIsDeclined(true);
    } catch (error) {
      console.error('Error declining transaction:', error);
    } finally {
      setIsDeclining(false);
    }
  };

  if (isDeclined) {
    return (
      <TxContainer
        style={{
          backgroundColor: messageBackgroundColor,
          borderColor,
          textColor,
        }}
      >
        <div className="bitte-p-6 bitte-text-center">
          <X className="bitte-mx-auto bitte-mb-2 bitte-h-8 bitte-w-8 bitte-text-red-500" />
          <p>Signature request declined</p>
        </div>
      </TxContainer>
    );
  }

  // Only show full loading overlay for declining (not signing)
  if (isDeclining) {
    return (
      <TxContainer
        style={{
          backgroundColor: messageBackgroundColor,
          borderColor,
          textColor,
        }}
      >
        <div className="bitte-p-6">
          <div>
            <LoadingMessage color={textColor} />
            <p className="bitte-text-center bitte-text-sm">
              Declining signature request...
            </p>
          </div>
        </div>
      </TxContainer>
    );
  }

  const getSignTypeLabel = () => {
    if (isTypedData) return 'Typed Data';
    if (isMessage) return 'Message';
    return 'Data';
  };

  // decode message for display
  const rawData =
    method == 'personal_sign'
      ? params[0]
      : (params[1] as `0x${string}` | string);
  const displayMessage = isHex(rawData)
    ? decodeHexMessage(rawData)
    : JSON.stringify(JSON.parse(rawData), null, 2);

  return (
    <TxContainer
      style={{
        backgroundColor: messageBackgroundColor,
        borderColor,
        textColor,
      }}
    >
      <CardHeader className="bitte-p-4 bitte-text-center bitte-md:p-6">
        <div className="bitte-flex bitte-items-center bitte-justify-center bitte-gap-2">
          <p className="bitte-text-xl bitte-font-semibold">
            {classification.title}
          </p>
        </div>
        <p className="bitte-mt-2 bitte-text-xs bitte-opacity-80">
          You are being asked to sign typed data.
          <br />
          This action does not cost gas or transfer any tokens.
        </p>
      </CardHeader>

      <div className="bitte-p-6">
        <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
          <TransactionDetail
            label="Network"
            value={
              <div className="bitte-flex bitte-items-center bitte-gap-2">
                {network?.icon && (
                  <img
                    src={network.icon}
                    alt={network.name}
                    className="bitte-h-4 bitte-w-4"
                  />
                )}
                <span>{network?.name || ''}</span>
              </div>
            }
          />

          <TransactionDetail label="Method" value={method} />

          {evmAddress && (
            <TransactionDetail
              label="Address"
              value={
                <div className="bitte-flex bitte-items-center bitte-gap-1 bitte-min-w-0">
                  <CopyStandard text={evmAddress} textSize="sm" />
                </div>
              }
            />
          )}

          <div className="bitte-rounded-lg bitte-bg-gray-900 bitte-p-3 bitte-border bitte-border-gray-700">
            <Accordion type="single" collapsible>
              <AccordionItem
                value="content-to-sign"
                className="bitte-border-none"
              >
                <AccordionTrigger className="bitte-text-sm bitte-text-white bitte-hover:text-gray-300 bitte-justify-between bitte-items-center">
                  <span className="bitte-flex-shrink-0">Message</span>
                  <div onClick={(e) => e.stopPropagation()}>
                    <CopyStandard text={displayMessage} textSize="sm" />
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="bitte-max-h-[250px] bitte-overflow-y-auto">
                    <p className="bitte-text-sm bitte-whitespace-pre-wrap bitte-break-all bitte-text-white">
                      {displayMessage}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {isHex(rawData) && (
            <div>
              <Accordion type="single" collapsible>
                <AccordionItem value="raw-data" className="bitte-border-none">
                  <AccordionTrigger className="bitte-text-sm bitte-text-gray-400 bitte-hover:text-gray-300">
                    View Raw Hex Data
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="bitte-rounded-lg bitte-bg-gray-900 bitte-border bitte-border-gray-700 bitte-p-3 bitte-max-h-[250px] bitte-overflow-y-auto">
                      <p className="bitte-text-xs bitte-font-mono bitte-break-all bitte-text-white">
                        {rawData}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}

          <div className="bitte-rounded-lg bitte-bg-blue-500/10 bitte-border bitte-border-gray-700 bitte-p-3">
            <div className="bitte-flex bitte-items-center bitte-gap-2 bitte-text-blue-600 bitte-dark:text-blue-400">
              <MessageSquare className="bitte-h-4 bitte-w-4" />
              <span className="bitte-font-medium">No Gas Required</span>
            </div>
            <p className="bitte-mt-1 bitte-text-xs bitte-text-blue-500 bitte-dark:text-blue-300">
              Signing this {getSignTypeLabel().toLowerCase()} does not cost any
              gas or transfer tokens.
            </p>
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="bitte-p-4">
          <div className="bitte-rounded-lg bitte-bg-red-500/10 bitte-border bitte-border-gray-700 bitte-p-3">
            <p className="bitte-text-red-500 bitte-text-sm bitte-break-all">
              {errorMsg}
            </p>
          </div>
        </div>
      )}

      {isSuccess ? (
        <div className="bitte-p-4">
          <div className="bitte-rounded-lg bitte-bg-green-500/10 bitte-border bitte-border-green-500/20 bitte-p-3 bitte-text-center">
            <Check className="bitte-mx-auto bitte-mb-2 bitte-h-6 bitte-w-6 bitte-text-green-500" />
            <p className="bitte-text-green-500 bitte-font-semibold bitte-text-sm">
              Message signed successfully!
            </p>
          </div>
        </div>
      ) : (
        <SigningCardFooter
          messageId={messageId}
          isLoading={isLoading}
          isSuccess={isSuccess}
          errorMsg={errorMsg}
          textColor={textColor}
          onSign={handleSmartAction}
          onDecline={declineTransaction}
          DeclineButton={DeclineButton}
          ApproveButton={ApproveButton}
          isDeclining={isDeclining}
        />
      )}
    </TxContainer>
  );
};
